/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/* eslint-disable check-i18n-id-prefix/i18n-id-prefix-with-package-name-FormattedMessage */
import { Fragment, type FC, useEffect, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { Query } from 'react-apollo';
import { styled } from '@compiled/react';
// We have deprecated unstated. Please use react-sweet-state instead
import { Subscribe } from 'unstated';

import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import AkQuestionCircleIcon from '@atlaskit/icon/core/question-circle';
import AkShortcutIcon from '@atlaskit/icon/core/link-external';
import { ModalTransition } from '@atlaskit/modal-dialog';
import AkTooltip from '@atlaskit/tooltip/Tooltip';
import { IconButton } from '@atlaskit/button/new';
import { ButtonItem, LinkItem, MenuGroup } from '@atlaskit/menu';
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { DevToolsDialog } from '@confluence/dev-tools';
import { GlobalFeedbackCollector, FeedbackSource } from '@confluence/global-feedback-collector';
import { ShortcutVisualizer } from '@confluence/shortcuts';
import { UserContextQuery, isAtlassianStaff } from '@confluence/navigation-search-dialog';
import type { UserContextQueryResults } from '@confluence/navigation-search-dialog';
import { PageSubscriber } from '@confluence/page-context';
import { fg } from '@confluence/feature-gating';
import { componentWithFG } from '@confluence/feature-gate-component';
import { LoadableLazy } from '@confluence/loadable';
import { useBlueprintType } from '@confluence/editor-features';

import { HelpCenterStateContainer } from './HelpCenterStateContainers';
import { isDescendant } from './helpers';
import {
	EDITOR_IMPROVEMENTS_URL,
	EDITOR_TEMPLATES_URL,
	CONTEXTUAL_EDITOR_HELP_LINKS,
	PREDEFINED_BLUEPRINT_TYPES_TITLES,
} from './constants';
import { HelpMenuHelpCenterItem } from './HelpMenuHelpCenterItem';
import { FeedbackItemWrapper } from './FeedbackItemWrapper';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Root = styled.div({
	position: 'relative',
	display: 'inline-flex',
});

const styles = cssMap({
	base: {
		backgroundColor: token('elevation.surface.overlay'),
		boxShadow: token('elevation.shadow.overlay'),
		borderRadius: token('border.radius'),
		paddingBlock: token('space.075'),
		marginBlock: token('space.100'),
		marginInline: 'auto',
		maxWidth: '320px',
	},
	shortcut: {
		color: token('color.text.subtlest'),
	},
});

const MenuItem = componentWithFG(
	'confluence_frontend_editor_help_center_performance',
	ButtonItem,
	DropdownItem,
);

export type HelpMenuComponentProps = {
	openHelpMenu?: () => void;
	closeHelpMenu?: () => void;
	openHelpDialog: () => void;
	contentId: string;
	isNCSEditor?: boolean;
	isTemplateEditor?: boolean;
	isCompanyHub?: boolean;
	isOpen?: boolean;
	setIsOpen?: (isOpen: boolean) => void;
	setShouldShowDevTools?: (shouldShowDevTools: boolean) => void;
	setShouldShowGlobalFeedbackCollector?: (shouldShowGlobalFeedbackCollector: boolean) => void;
};

export const HelpMenuComponent: FC<HelpMenuComponentProps> = ({
	openHelpMenu,
	closeHelpMenu,
	openHelpDialog,
	contentId,
	isNCSEditor,
	isTemplateEditor,
	isCompanyHub,
	isOpen,
	setIsOpen,
	setShouldShowDevTools,
	setShouldShowGlobalFeedbackCollector,
}) => {
	const [container, setContainer] = useState<HTMLDivElement | null>(null);
	const { formatMessage } = useIntl();
	const blueprintType = useBlueprintType(contentId, isTemplateEditor);

	const handleCloseHelpMenu = useCallback(() => {
		if (fg('confluence_frontend_editor_help_center_performance')) {
			setIsOpen?.(false);
		} else {
			closeHelpMenu?.();
		}
	}, [setIsOpen, closeHelpMenu]);

	const onOpenChange = useCallback(() => {
		if (isOpen) {
			closeHelpMenu?.();
		} else {
			openHelpMenu?.();
		}
	}, [isOpen, openHelpMenu, closeHelpMenu]);

	const handleClickOutside = useCallback(
		(event) => {
			const target = event.target;
			const targetIsNotUnmountedByReact = target.offsetParent;
			if (isOpen && targetIsNotUnmountedByReact && !isDescendant(container, target)) {
				handleCloseHelpMenu();
			}
		},
		[isOpen, handleCloseHelpMenu, container],
	);

	useEffect(() => {
		window.document.addEventListener('click', handleClickOutside);
		return () => {
			window.document.removeEventListener('click', handleClickOutside);
		};
	}, [handleClickOutside]);

	const onItemClick = useCallback(
		(_event, callback) => {
			if (callback) {
				return () => {
					handleCloseHelpMenu();
					callback();
				};
			}
			handleCloseHelpMenu();
		},
		[handleCloseHelpMenu],
	);

	const renderHelpShortcutDropDownItem = useMemo(
		() =>
			fg('confluence_frontend_editor_help_center_performance') ? (
				<ButtonItem
					onClick={onItemClick(null, openHelpDialog)}
					iconAfter={<ShortcutVisualizer shortcut="mod+/" />}
				>
					<FormattedMessage id="editor.shortcuts" />
				</ButtonItem>
			) : (
				<DropdownItem
					onClick={onItemClick(null, openHelpDialog)}
					elemAfter={<ShortcutVisualizer shortcut="mod+/" />}
				>
					<FormattedMessage id="editor.shortcuts" />
				</DropdownItem>
			),
		[onItemClick, openHelpDialog],
	);

	/**
	 * The first time the help center is closed, it raises an unstated flag that
	 * causes the help menu to render the "help center" menu item with a
	 * chonboarding spotlight.
	 */
	const renderHelpCenter = useMemo(() => {
		return (
			<Subscribe to={[HelpCenterStateContainer]}>
				{(helpCenterState: HelpCenterStateContainer) => (
					<HelpMenuHelpCenterItem
						helpCenterState={helpCenterState}
						closeHelpMenu={handleCloseHelpMenu}
					/>
				)}
			</Subscribe>
		);
	}, [handleCloseHelpMenu]);

	const renderBlueprintTypeBasedDropdownItems = useMemo(() => {
		if (CONTEXTUAL_EDITOR_HELP_LINKS.hasOwnProperty(blueprintType)) {
			const i18nMessage = {
				id: PREDEFINED_BLUEPRINT_TYPES_TITLES[blueprintType],
			};
			const blueprintTitle = formatMessage(i18nMessage);
			return CONTEXTUAL_EDITOR_HELP_LINKS[blueprintType].map((item, index) => (
				<MenuItem
					key={index}
					onClick={(e) => onItemClick(e, null)}
					href={item.href}
					target="_blank"
				>
					<FormattedMessage
						id="editor.help.how.to.use.blueprint"
						values={{
							blueprintType: blueprintTitle,
						}}
					/>
				</MenuItem>
			));
		}
		return null;
	}, [blueprintType, formatMessage, onItemClick]);

	const renderDevTools = useMemo(() => {
		return (
			<Query<UserContextQueryResults> query={UserContextQuery}>
				{({ data }) => {
					const atlassianStaff = isAtlassianStaff(data);
					return atlassianStaff ? (
						<Subscribe to={[HelpCenterStateContainer]}>
							{(helpCenterState: HelpCenterStateContainer) => (
								<MenuItem
									onClick={() => {
										handleCloseHelpMenu();
										if (fg('confluence_frontend_editor_help_center_performance')) {
											setShouldShowDevTools?.(true);
										} else {
											void helpCenterState.showDevTools();
										}
									}}
								>
									<FormattedMessage
										id="editor.help.dev-tools"
										defaultMessage="Dev Tools"
										description="Menu item for internal developer tools"
									/>
								</MenuItem>
							)}
						</Subscribe>
					) : null;
				}}
			</Query>
		);
	}, [handleCloseHelpMenu, setShouldShowDevTools]);

	const renderExternalLinkItems = useMemo(() => {
		if (isTemplateEditor) {
			return null;
		}

		return (
			<Fragment>
				{fg('confluence_frontend_editor_help_center_performance') ? (
					<LinkItem
						href={EDITOR_IMPROVEMENTS_URL}
						target="_blank"
						iconAfter={
							<AkShortcutIcon color="currentColor" label={formatMessage({ id: 'help.name' })} />
						}
					>
						<FormattedMessage id="editor.help.updates" />
					</LinkItem>
				) : (
					<DropdownItem
						elemAfter={
							<AkShortcutIcon color="currentColor" label={formatMessage({ id: 'help.name' })} />
						}
						href={EDITOR_IMPROVEMENTS_URL}
						target="_blank"
					>
						<FormattedMessage id="editor.help.updates" />
					</DropdownItem>
				)}
				{!isCompanyHub ? (
					fg('confluence_frontend_editor_help_center_placeholder') ? (
						<LinkItem
							href={EDITOR_TEMPLATES_URL}
							target="_blank"
							iconAfter={
								<AkShortcutIcon color="currentColor" label={formatMessage({ id: 'help.name' })} />
							}
						>
							<FormattedMessage id="editor.help.templates" defaultMessage="Use page templates" />
						</LinkItem>
					) : (
						<DropdownItem
							elemAfter={
								<AkShortcutIcon color="currentColor" label={formatMessage({ id: 'help.name' })} />
							}
							href={EDITOR_TEMPLATES_URL}
							target="_blank"
						>
							<FormattedMessage id="editor.help.templates" defaultMessage="Use page templates" />
						</DropdownItem>
					)
				) : null}
			</Fragment>
		);
	}, [isTemplateEditor, isCompanyHub, formatMessage]);

	const renderNCSSynchronyIndicator = useMemo(
		() => (
			<Query<UserContextQueryResults> query={UserContextQuery}>
				{({ data }) => {
					const atlassianStaff = isAtlassianStaff(data);

					if (!atlassianStaff) {
						return null;
					}

					if (isTemplateEditor) {
						return null;
					}

					const indicator = isNCSEditor ? (
						<FormattedMessage
							id="editor.help.dev-only-ncs-editor"
							defaultMessage="DEV ONLY: NCS editor"
							description="Dev Menu item indicator for NCS editor"
						/>
					) : (
						<FormattedMessage
							id="editor.help.dev-only-synchrony-editor"
							defaultMessage="DEV ONLY: Synchrony editor"
							description="Dev Menu item indicator for Synchrony editor"
						/>
					);

					return <DropdownItem>{indicator}</DropdownItem>;
				}}
			</Query>
		),
		[isNCSEditor, isTemplateEditor],
	);

	const renderFeedbackItem = useMemo(() => {
		const openGlobalFeedbackCollector = () => {
			setShouldShowGlobalFeedbackCollector?.(true);
		};
		return (
			<ButtonItem onClick={onItemClick(null, openGlobalFeedbackCollector)}>
				<FormattedMessage
					id="editor-help.fabric.help.center.feedback.item"
					defaultMessage="Feedback"
				/>
			</ButtonItem>
		);
	}, [onItemClick, setShouldShowGlobalFeedbackCollector]);

	const renderHelpDropDown = () => (
		<DropdownMenu<HTMLButtonElement>
			isOpen={isOpen}
			trigger={({ triggerRef, ...props }) => (
				<AkTooltip content={formatMessage({ id: 'help.name' })} position="right" hideTooltipOnClick>
					<IconButton
						{...props}
						icon={AkQuestionCircleIcon}
						shape="circle"
						appearance="subtle"
						label={formatMessage({ id: 'help.name' })}
						ref={triggerRef}
						testId="help-menu-icon"
						onClick={onOpenChange}
						aria-expanded={isOpen}
					/>
				</AkTooltip>
			)}
			shouldRenderToParent={fg('should-render-to-parent-should-be-truepage-experie')}
		>
			<DropdownItemGroup testId="help-menu-dropdown-item-group">
				{renderNCSSynchronyIndicator}
				{renderDevTools}

				{renderHelpShortcutDropDownItem}

				{renderExternalLinkItems}
				{renderHelpCenter}
				{renderBlueprintTypeBasedDropdownItems}
				<DropdownItem onClick={(e) => onItemClick(e, null)}>
					<FeedbackItemWrapper />
				</DropdownItem>
			</DropdownItemGroup>
		</DropdownMenu>
	);

	if (fg('confluence_frontend_editor_help_center_performance')) {
		return (
			<div css={styles.base} data-testid="help-menu-component" ref={setContainer}>
				<MenuGroup>
					{renderNCSSynchronyIndicator}
					{renderDevTools}
					{renderHelpShortcutDropDownItem}
					{renderExternalLinkItems}
					{renderHelpCenter}
					{renderBlueprintTypeBasedDropdownItems}
					{renderFeedbackItem}
				</MenuGroup>
			</div>
		);
	}

	return (
		<Root ref={setContainer}>
			{renderHelpDropDown()}

			<Subscribe to={[HelpCenterStateContainer]}>
				{(helpCenterState: HelpCenterStateContainer) => (
					<PageSubscriber>
						{({ contentType }) => (
							<Fragment>
								<ModalTransition>
									{helpCenterState.state.showDevTools ? (
										<DevToolsDialog onClose={helpCenterState.hideDevTools} />
									) : null}
								</ModalTransition>
								<ModalTransition>
									{helpCenterState.state.showGlobalFeedbackCollector ? (
										<GlobalFeedbackCollector
											feedbackSource={FeedbackSource.EDITOR}
											onClose={helpCenterState.hideGlobalFeedbackCollector}
											onCloseComplete={() => {}}
											contentType={contentType}
										/>
									) : null}
								</ModalTransition>
							</Fragment>
						)}
					</PageSubscriber>
				)}
			</Subscribe>
		</Root>
	);
};

export const LazyHelpMenu = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-HelpMenuComponent" */ './HelpMenuComponent'))
			.HelpMenuComponent,
});
